import Cookies from 'js-cookie';

// Synchronous, first-paint answer to "does the signed-in user have full scope
// over the current account?".
//
// It mirrors /meta's `role`, which is the server's `accessForUser().isOwner`
// (services/cognitive-flows/workspaceService.js): platform role
// admin/developer, OR users.permissions contains `owner:<shopKey>` or
// `account_admin:<shopKey>`. All of those inputs are already in cookies, so the
// gate no longer has to wait for GET /meta — waiting is what made the Team page
// flash the owner UI at members before swapping to the denial notice.
//
// It answers "full scope", NOT "is THE owner". The narrower question needs
// /meta's `accountRole` / `can` block, because an Admin looks identical here.
//
// This is a RENDERING HINT, never an authorization decision. Cookies are
// client-writable; every owner-only route is still gated server-side by
// requireAccountOwner, and bootstrap() overwrites the seed with /meta's answer
// as soon as it lands.

// Mirrors server/services/cognitive-flows/shopKey.js normalizeShopKey. The
// `shop` cookie is written stripped at login (authCookies.js) but with the
// full domain on a store switch (Sidebar.jsx), so both shapes must normalize.
function normalizeShopKey(value) {
    if (value === undefined || value === null || String(value).trim() === '') return null;
    return String(value).trim().toLowerCase().replace(/\.myshopify\.com$/i, '');
}

// Platform roles that are implicitly owners of every account they can see.
const PLATFORM_OWNER_ROLES = new Set(['admin', 'developer']);

/**
 * @returns {'owner' | 'member' | null} `null` means "can't tell from cookies" —
 * callers must fall back to the async answer. That case is real: Shopify OAuth
 * sessions don't set user_role / user_permissions, and denying a genuine owner
 * off a missing cookie would be worse than the flicker this replaces.
 */
export function seedAccountRole() {
    try {
        const role = Cookies.get('user_role');
        if (role && PLATFORM_OWNER_ROLES.has(role)) return 'owner';

        const permissionsRaw = Cookies.get('user_permissions');
        const shopKey = normalizeShopKey(Cookies.get('shop'));
        // Undecidable without both the permission list and the shop it is keyed by.
        if (permissionsRaw === undefined || !shopKey) return null;

        const permissions = JSON.parse(permissionsRaw);
        if (!Array.isArray(permissions)) return null;
        // Admins have full scope too — seeding them as 'member' would leave every
        // manager-only surface flashing on first paint until /meta lands.
        const fullScope = permissions.includes(`owner:${shopKey}`)
            || permissions.includes(`account_admin:${shopKey}`);
        return fullScope ? 'owner' : 'member';
    } catch {
        // Malformed cookie — same as absent.
        return null;
    }
}
